import {
  BriefcaseFilled,
  ClockFilled,
  DismissFilled,
  EditFilled,
  PersonFilled,
} from '@fluentui/react-icons';
import { AbsenceReasonCategory, Person } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import cn from 'classnames';
import React from 'react';

import { LazyAvatarUser } from '../../../Avatar';
import { Button, ButtonProps } from '../../../Button';
import { BroomIcon } from '../../../Icons';
import { Tag, TagColorEnum, TagProps } from '../../../Tags/Tags';
import { stripScriptTags } from '../../../Utils';
import './TeachingUnitEntry.scss';
import { TeachingUnitEntrySkeleton } from './TeachingUnitEntrySkeleton';

export enum TeachingUnitEntryType {
  Homework = 'Homework',
  // Klassenbucheintrag
  ClassBookEntry = 'ClassBookEntry',
  // Stundeninhalte
  EducationalMaterial = 'EducationalMaterial',
  Absence = 'Absence',
  AbsenceLate = 'AbsenceLate',
  AbsenceEarly = 'AbsenceEarly',
  Services = 'Services',
  Present = 'Present',
}

type ActionItemProps = ButtonProps;

export enum TeachingUnitEntryIconColor {
  Red = 'red',
  Yellow = 'yellow',
  Primary = 'primary',
  Green = 'green',
}

export interface TeachingUnitEntryProps {
  type: TeachingUnitEntryType;
  headline: string;
  subheadline?: string;
  tags?: TagProps[];
  actions?: ActionItemProps[];
  author?: Person;
  onClick?: () => void;
  loading?: boolean;
}

const getIconForType = (type: TeachingUnitEntryType) => {
  switch (type) {
    case TeachingUnitEntryType.Homework:
      return <BriefcaseFilled />;
    case TeachingUnitEntryType.ClassBookEntry:
      return <PersonFilled />;
    case TeachingUnitEntryType.EducationalMaterial:
      return <EditFilled />;
    case TeachingUnitEntryType.Absence:
      return <DismissFilled />;
    case TeachingUnitEntryType.AbsenceLate:
    case TeachingUnitEntryType.AbsenceEarly:
      return <ClockFilled />;
    case TeachingUnitEntryType.Services:
      return <BroomIcon />;
  }
};

export const getTeachingUnitTypeForAbsenceReason = (
  absenceReasonCategory?: AbsenceReasonCategory,
): TeachingUnitEntryType => {
  if (!absenceReasonCategory) {
    return TeachingUnitEntryType.Present;
  }

  switch (absenceReasonCategory) {
    case AbsenceReasonCategory.late:
      return TeachingUnitEntryType.AbsenceLate;
    case AbsenceReasonCategory.leftEarly:
      return TeachingUnitEntryType.AbsenceEarly;
    default:
      return TeachingUnitEntryType.Absence;
  }
};

export const TeachingUnitEntry: React.FC<TeachingUnitEntryProps> = ({
  type,
  headline,
  subheadline = '',
  tags,
  actions,
  author,
  loading,
  onClick,
}) => {
  // The rich-text editor (Quill) stores every space as a non-breaking space
  // (U+00A0 / &nbsp;). That turns a long description into a single unbreakable
  // token, which either overflows the card (overflow-wrap:normal) or is chopped
  // mid-word (overflow-wrap:break-word). Normalising them back to regular
  // spaces lets the text wrap at word boundaries; genuine long tokens (URLs)
  // are still broken by the SCSS overflow-wrap rule only when necessary.
  const normalizedSubheadline = subheadline.replace(/&nbsp;|\u00a0/g, ' ');

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const interactiveProps = onClick
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: handleClick,
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        },
      }
    : {};

  let iconColor = TeachingUnitEntryIconColor.Primary;

  switch (type) {
    case TeachingUnitEntryType.Absence:
      iconColor = TeachingUnitEntryIconColor.Red;
      break;
    case TeachingUnitEntryType.AbsenceEarly:
    case TeachingUnitEntryType.AbsenceLate:
      iconColor = TeachingUnitEntryIconColor.Yellow;
      break;
  }

  return loading ? (
    <TeachingUnitEntrySkeleton />
  ) : (
    <div
      className={cn('flex items-stretch teachingUnitEntry', {
        clickableTeachingUnitEntry: onClick !== undefined,
      })}
    >
      <div className="flex flex-1 teachingUnitEntryClickArea" {...interactiveProps}>
        <div className={cn('iconWrapper', iconColor.toString())}>{getIconForType(type)}</div>
        <div className="teachingUnitEntryContent">
          <div className="teachingUnitEntryHeadline">{headline}</div>
          {author && (
            <div className="authorWrapper">
              <LazyAvatarUser name={author.displayName} size={20} userId={author.id} />
              <span className="authorName">{author.displayName}</span>
            </div>
          )}
          {normalizedSubheadline !== '' && type !== TeachingUnitEntryType.Homework && (
            <div className="subheadline">{normalizedSubheadline}</div>
          )}
          {normalizedSubheadline !== '' && type === TeachingUnitEntryType.Homework && (
            <div
              className="subheadline homeworkDescription"
              dangerouslySetInnerHTML={{
                __html: stripScriptTags(normalizedSubheadline),
              }}
            ></div>
          )}
          {tags !== undefined && tags.length > 0 && (
            <div className="tagWrapper">
              {tags.map((tag) => (
                <Tag color={TagColorEnum.Grey} label={tag.label} />
              ))}
            </div>
          )}
        </div>
      </div>
      {actions && actions.length > 0 && (
        <div className="actionsWrapper">
          {actions.map((action, index) => (
            <Button {...action} key={index} appearance="transparent" />
          ))}
        </div>
      )}
    </div>
  );
};
